/**
 * AgentTypeSelector - Dropdown pour sélectionner le type d'agent
 * 
 * Permet de choisir parmi les types d'agents prédéfinis pour afficher
 * les templates de prompts correspondants.
 */
import React from 'react'
import { ChevronDown, Check } from 'lucide-react'
import * as Select from '@radix-ui/react-select'
import { AgentType, getAgentTypes, getAgentTypeInfo } from '../data/promptTemplates'
import { useAppLanguage } from '../i18n'

interface AgentTypeSelectorProps {
  value: AgentType | null
  onChange: (type: AgentType | null) => void
  className?: string
}

export default function AgentTypeSelector({ value, onChange, className = '' }: AgentTypeSelectorProps) {
  const { language, t } = useAppLanguage()
  const ae = t.agents.agentEditor
  const agentTypes = getAgentTypes(language)
  const selectedType = value ? getAgentTypeInfo(value, language) : null

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-zinc-300 mb-2">
        {ae.agentTypeLabel}
      </label>
      
      <Select.Root
        value={value || "none"}
        onValueChange={(val) => onChange(val === "none" ? null : val as AgentType)}
      >
        <Select.Trigger
          className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 cursor-pointer hover:border-zinc-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-colors flex items-center justify-between"
        >
          <Select.Value placeholder={ae.agentTypePlaceholder} />
          <Select.Icon>
            <ChevronDown className="w-5 h-5 text-zinc-400" />
          </Select.Icon>
        </Select.Trigger>
        <Select.Portal>
          <Select.Content
            className="overflow-hidden bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-50"
            position="popper"
            sideOffset={5}
          >
            <Select.Viewport className="p-1">
              <Select.Item
                value="none"
                className="relative flex items-center px-8 py-2 text-sm text-zinc-100 rounded-md outline-none cursor-pointer hover:bg-zinc-700 focus:bg-zinc-700 data-[state=checked]:bg-emerald-900/30"
              >
                <Select.ItemIndicator className="absolute left-2 inline-flex items-center">
                  <Check className="w-4 h-4 text-emerald-400" />
                </Select.ItemIndicator>
                <Select.ItemText>{ae.agentTypePlaceholder}</Select.ItemText>
              </Select.Item>
              {agentTypes.map(type => (
                <Select.Item
                  key={type.id}
                  value={type.id}
                  className="relative flex items-center px-8 py-2 text-sm text-zinc-100 rounded-md outline-none cursor-pointer hover:bg-zinc-700 focus:bg-zinc-700 data-[state=checked]:bg-emerald-900/30"
                >
                  <Select.ItemIndicator className="absolute left-2 inline-flex items-center">
                    <Check className="w-4 h-4 text-emerald-400" />
                  </Select.ItemIndicator>
                  <Select.ItemText>{type.icon} {type.label}</Select.ItemText>
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>

      {selectedType && (
        <div className="mt-3 p-3 bg-zinc-800/50 border border-zinc-700/50 rounded-lg">
          <p className="text-sm text-zinc-300 mb-2">
            {selectedType.description}
          </p>
          <div className="flex flex-wrap gap-2">
            {selectedType.useCases.map((useCase, idx) => (
              <span
                key={idx}
                className="inline-flex items-center px-2 py-1 rounded-md bg-emerald-900/20 text-emerald-400 text-xs"
              >
                {useCase}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
